/**
 * Токены приёма данных от 1С.
 *
 * Токен показывается один раз — при выдаче. Дальше в базе только хэш,
 * поэтому «покажите, какой у нас был токен» — вопрос без ответа: выдать
 * новый и отозвать старый. Это неудобно ровно один раз и защищает от того,
 * что токен уедет в переписку, а вместе с ней куда угодно.
 */

import { useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api'
import type { IngestToken } from '../types'
import { Badge, Card, Field, Loading, Notice } from '../components/ui'
import { formatDateTime } from '../format'

export function AdminTokens() {
  const [tokens, setTokens] = useState<IngestToken[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<{ token: string; name: string } | null>(null)
  const [draft, setDraft] = useState({ name: '', database_id: '', streams: '' })
  const [busy, setBusy] = useState(false)
  // Двойной клик приходит раньше, чем кнопка станет заблокированной.
  const inFlight = useRef(false)

  const load = () =>
    api
      .ingestTokens()
      .then((data) => {
        setTokens(data.tokens)
        setError(null)
      })
      .catch((caught) => setError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить токены'))

  useEffect(() => {
    void load()
  }, [])

  if (error && !tokens) return <Notice tone="bad">{error}</Notice>
  if (!tokens) return <Loading rows={5} />

  const issue = async () => {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      const result = await api.issueIngestToken({
        name: draft.name.trim(),
        database_id: draft.database_id.trim() || undefined,
        streams: draft.streams
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean),
      })
      setIssued({ token: result.token, name: draft.name.trim() })
      setDraft({ name: '', database_id: '', streams: '' })
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось выдать токен')
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const revoke = async (token: IngestToken) => {
    const reason = window.prompt(`Причина отзыва токена «${token.name}»`, 'Работы завершены')
    if (reason === null || inFlight.current) return

    inFlight.current = true
    setBusy(true)
    try {
      await api.revokeIngestToken(token.id, reason)
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось отозвать токен')
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const active = tokens.filter((token) => !token.revoked_at)

  return (
    <div className="stack" style={{ gap: 18 }}>
      <Notice tone="info">
        Токен даёт право писать в хранилище. Пока не выдан ни один, работает запасной токен из переменной
        окружения — режим локального запуска. Как только появляется первый настоящий, запасной перестаёт
        действовать.
      </Notice>

      {error && <Notice tone="bad">{error}</Notice>}

      {issued && (
        <Notice tone="warn" icon="warning">
          <div className="stack" style={{ gap: 6 }}>
            <span>
              Токен для «{issued.name}» выдан. Он показывается <strong>один раз</strong>: скопируйте сейчас,
              восстановить его нельзя.
            </span>
            <code className="token-value">{issued.token}</code>
            <button className="btn btn--sm" onClick={() => setIssued(null)}>
              Я скопировал
            </button>
          </div>
        </Notice>
      )}

      <Card
        title={
          <div>
            <h3>Выданные токены</h3>
            <div className="tiny muted">
              Действующих: {active.length}. Молчащий токен — первый признак того, что обмен встал.
            </div>
          </div>
        }
        flush
      >
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Кому выдан</th>
                <th>Префикс</th>
                <th>Ограничения</th>
                <th>Последнее обращение</th>
                <th>Пакетов</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => (
                <tr key={token.id} style={token.revoked_at ? { opacity: 0.55 } : undefined}>
                  <td>
                    <div>{token.name}</div>
                    <div className="tiny muted">
                      выдан {formatDateTime(token.created_at)}
                      {token.created_by ? `, ${token.created_by}` : ''}
                    </div>
                  </td>
                  <td>
                    <code className="tiny">{token.prefix}…</code>
                  </td>
                  <td className="tiny">
                    {token.database_id ? <div>база: {token.database_id}</div> : null}
                    {token.streams.length > 0 ? <div>потоки: {token.streams.join(', ')}</div> : null}
                    {!token.database_id && token.streams.length === 0 ? (
                      <span className="muted">без ограничений</span>
                    ) : null}
                  </td>
                  <td className="tiny">
                    {token.last_used_at ? (
                      <>
                        {formatDateTime(token.last_used_at)}
                        {token.last_used_ip ? <div className="muted">{token.last_used_ip}</div> : null}
                      </>
                    ) : (
                      <span className="muted">ни разу</span>
                    )}
                  </td>
                  <td>{token.use_count}</td>
                  <td>
                    {token.revoked_at ? (
                      <Badge tone="bad">отозван</Badge>
                    ) : (
                      <button
                        className="btn btn--sm btn--ghost btn--danger"
                        disabled={busy}
                        onClick={() => void revoke(token)}
                      >
                        Отозвать
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Выдать токен">
        <div className="grid grid--2">
          <Field label="Кому выдан" hint="По этому названию его потом отзывают">
            <input
              className="input"
              value={draft.name}
              placeholder="1С УТ 11.5, боевая база"
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </Field>
          <Field label="База" hint="Пусто — любая">
            <input
              className="input"
              value={draft.database_id}
              placeholder="ut11.5"
              onChange={(e) => setDraft({ ...draft, database_id: e.target.value })}
            />
          </Field>
          <Field label="Потоки" hint="Через запятую. Пусто — все">
            <input
              className="input"
              value={draft.streams}
              placeholder="sales, dictionaries"
              onChange={(e) => setDraft({ ...draft, streams: e.target.value })}
            />
          </Field>
        </div>

        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn btn--primary" disabled={busy || !draft.name.trim()} onClick={() => void issue()}>
            Выдать
          </button>
          <span className="tiny muted">Токен будет показан один раз.</span>
        </div>
      </Card>
    </div>
  )
}
